/**
 * Client half of the daily activity meter.
 *
 * The server records at most one `userlogons` row per user per calendar day
 * (`UserRepository.RecordSessionActivityAsync`), and `dbo.LeagueRefreshAll` scores a
 * league on the count of DISTINCT DAYS each of its owners appeared. The server is the
 * authority on what counts as a day; everything here is throttling, not truth.
 *
 * Why a client throttle exists at all: the only thing that triggers the recording call
 * is `GET /User/CurrentUser`, which the SPA fires once per `UserProvider` mount — i.e.
 * once per full page load. A user who leaves a tab open for a week registers a single
 * day. `UserContext` re-pings when the tab is brought back into view, and this module
 * keeps that from becoming a request per tab switch.
 *
 * ⚠️ Deliberately a rolling interval, NOT a "have I pinged today?" date stamp. A local
 * calendar-day marker looks tidier and is wrong: `dbo.getdate()` is local Eastern, so a
 * viewer in another timezone rolls over to a new local date at a different moment than
 * the server does. Such a marker would suppress the ping across the server's real
 * midnight and lose that user roughly every other active day.
 */

const STORAGE_KEY = 'csfbl:activity-ping'

/** One hour. Well under a day, so the server's per-day check still does the real work. */
export const PING_THROTTLE_MS = 60 * 60 * 1000

/**
 * Fallback when localStorage is unavailable (private mode, blocked site data).
 * `null` means "never pinged" — deliberately not 0, which would only behave correctly
 * because real epoch values happen to exceed the throttle window.
 */
let memoryLast: number | null = null

function readLast(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw !== null) {
      const parsed = Number(raw)
      return Number.isFinite(parsed) ? parsed : null
    }
  } catch {
    // Storage blocked — throttle in memory for this page's lifetime instead.
  }
  return memoryLast
}

export function shouldPing(now: number = Date.now()): boolean {
  const last = readLast()
  if (last === null) return true
  return now - last >= PING_THROTTLE_MS
}

export function markPinged(now: number = Date.now()): void {
  memoryLast = now
  try {
    localStorage.setItem(STORAGE_KEY, String(now))
  } catch {
    // Storage blocked — memoryLast above already covers this page.
  }
}

/** Test seam: clears both the stored and in-memory marks. */
export function resetActivityPingThrottle(): void {
  memoryLast = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
